import { BN_MAX_PRINT_INCHES } from "@/lib/mockup-geometry.js";
import { calculateArtworkGeometry } from "@/lib/geometry.js";
import { retrieveErrors } from "@/lib/api-errors.js";

export const UPLOAD_URL = "/submission/upload";

// vue-dropzone (Dropzone.js) SUIT-twin preview template. This is the library's
// EXACT default preview template with additive `.nylon-Dropzone-*` twin classes
// so our authored CSS targets the twins, not the vendor `.dz-*` names. Every
// `.dz-*` class + `data-dz-*` JS hook is preserved (Dropzone needs them). Only
// the in-template structural elements get twins — the JS-toggled state classes
// (`.dz-drag-hover`/`.dz-started`/`.dz-success`), the JS-added `.dz-remove`
// link, and the wrapper `.dz-message` have no declarative hook (Dropzone.js
// has no option to rename state classes) and stay as a documented exception in
// `_NylonSubmitUpload.scss`, scoped under the `.nylon-Dropzone` container twin.
export const DROPZONE_PREVIEW_TEMPLATE = `<div class="dz-preview dz-file-preview nylon-Dropzone-preview">
  <div class="dz-image nylon-Dropzone-image"><img data-dz-thumbnail /></div>
  <div class="dz-details nylon-Dropzone-details">
    <div class="dz-size nylon-Dropzone-size"><span data-dz-size></span></div>
    <div class="dz-filename nylon-Dropzone-filename"><span data-dz-name></span></div>
  </div>
  <div class="dz-progress nylon-Dropzone-progress">
    <span class="dz-upload nylon-Dropzone-upload" data-dz-uploadprogress></span>
  </div>
  <div class="dz-error-message nylon-Dropzone-errorMessage"><span data-dz-errormessage></span></div>
  <div class="dz-success-mark nylon-Dropzone-successMark">
    <svg width="54" height="54" viewBox="0 0 54 54" fill="white" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.2071 29.7929L14.2929 25.7071C14.6834 25.3166 15.3166 25.3166 15.7071 25.7071L21.2929 31.2929C21.6834 31.6834 22.3166 31.6834 22.7071 31.2929L38.2929 15.7071C38.6834 15.3166 39.3166 15.3166 39.7071 15.7071L43.7929 19.7929C44.1834 20.1834 44.1834 20.8166 43.7929 21.2071L22.7071 42.2929C22.3166 42.6834 21.6834 42.6834 21.2929 42.2929L10.2071 31.2071C9.81658 30.8166 9.81658 30.1834 10.2071 29.7929Z" />
    </svg>
  </div>
  <div class="dz-error-mark nylon-Dropzone-errorMark">
    <svg width="54" height="54" viewBox="0 0 54 54" fill="white" xmlns="http://www.w3.org/2000/svg">
      <path d="M26.2929 20.2929L19.2071 13.2071C18.8166 12.8166 18.1834 12.8166 17.7929 13.2071L13.2071 17.7929C12.8166 18.1834 12.8166 18.8166 13.2071 19.2071L20.2929 26.2929C20.6834 26.6834 20.6834 27.3166 20.2929 27.7071L13.2071 34.7929C12.8166 35.1834 12.8166 35.8166 13.2071 36.2071L17.7929 40.7929C18.1834 41.1834 18.8166 41.1834 19.2071 40.7929L26.2929 33.7071C26.6834 33.3166 27.3166 33.3166 27.7071 33.7071L34.7929 40.7929C35.1834 41.1834 35.8166 41.1834 36.2071 40.7929L40.7929 36.2071C41.1834 35.8166 41.1834 35.1834 40.7929 34.7929L33.7071 27.7071C33.3166 27.3166 33.3166 26.6834 33.7071 26.2929L40.7929 19.2071C41.1834 18.8166 41.1834 18.1834 40.7929 17.7929L36.2071 13.2071C35.8166 12.8166 35.1834 12.8166 34.7929 13.2071L27.7071 20.2929C27.3166 20.6834 26.6834 20.6834 26.2929 20.2929Z" />
    </svg>
  </div>
</div>`;

// Dropzone `error` option for UPLOAD_URL: Dropzone's default writes `message`
// into the preview as-is (unwrapping only `message.error`), but a rejected
// upload answers with Laravel's 422 {message, errors}. Dropzone's own
// client-side checks (file type, size) pass a string.
export function showDropzoneError(file, message) {
  if (!file.previewElement) {
    return;
  }

  const text = typeof message === "string" ? message : retrieveErrors({ response: { data: message } });

  file.previewElement.classList.add("dz-error");
  file.previewElement.querySelectorAll("[data-dz-errormessage]").forEach((node) => {
    node.textContent = text;
  });
}

export const IMAGE_URL = "/image";
export const CLOTHING_MOCKUP_URL = "/mockup/clothing";
export const PHONECASE_MOCKUP_URL = "/mockup/phonecase";
export const HAT_MOCKUP_URL = "/mockup/hat";
export const BEANIE_MOCKUP_URL = "/mockup/beanie";
export const TOTE_MOCKUP_URL = "/mockup/tote";
export const DRINKWARE_MOCKUP_URL = "/mockup/drinkware";
export const DRINKWARE_TEXTURE_URL = "/mockup/drinkware-submission-texture";
export const STICKER_MOCKUP_URL = "/mockup/sticker";

// The pre-upload "your artwork goes here" dashed print-area border, rendered
// through the real mockup pipeline (the server maps this literal path to a
// repo-shipped PNG — see MockupController::PLACEHOLDER_ARTWORK_PATH). Used
// wherever a print location exists but its artwork hasn't been uploaded yet.
export const PLACEHOLDER_ARTWORK_PATH_INFO = { dirname: "placeholder", basename: "dashed-box.png" };

// The same placeholder in dark grey, for items whose print surface is white
// (the sticker), where the white dashes would vanish.
export const PLACEHOLDER_DARK_ARTWORK_PATH_INFO = { dirname: "placeholder", basename: "dashed-box-dark.png" };

// The garment every apparel preview renders before a color is chosen —
// Comfort Colors 1717 in Pepper (catalog color 1063, has front + back
// poses). Shared by the wizard's entry placeholder and the preview panel's
// no-selection state so the mockup never swaps blanks mid-flow.
export const DEFAULT_PREVIEW_BLANK = Object.freeze({ blankId: "1717", colorId: 1063 });

// The drinkware model (and its camera) the 3D preview shows before an item is
// chosen — the steel Adventure Tumbler the server's texture endpoint also
// defaults to (tA20sSteel). Shared by the wizard's entry placeholder and the
// preview panel's no-selection state.
export const DEFAULT_PREVIEW_DRINKWARE = Object.freeze({
  model: "/img/drinkware/gltf/low_poly/adventure_tumbler_steel/adventure_tumbler_steel_low_v2.glb",
  camera: Object.freeze({
    cameraOrbit: "115deg auto auto",
    far: null,
    fieldOfView: "auto",
    maxCameraOrbit: "auto 120deg auto",
    maxFieldOfView: "auto",
    minCameraOrbit: "auto 60deg auto",
    minFieldOfView: "auto",
    near: 1,
  }),
});

export function getXsrfToken() {
  const match = document.cookie.match(new RegExp("(^|;\\s*)XSRF-TOKEN=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : "";
}

export const productTypes = {
  APPAREL: "clothing",
  PHONECASE: "phonecase",
  HAT: "hat",
  BEANIE: "beanie",
  TOTE: "tote",
  DRINKWARE: "drinkware",
  STICKER: "sticker",
};

export const salesMethods = {
  ONDEMAND: "on-demand",
  CAMPAIGN: "campaign",
  STOCK: "stock",
};

export const productionMethods = {
  SCREENPRINT: "screen print",
  DIRECTTOGARMENT: "direct to garment",
  DIRECTTOFILM: "direct to film",
  EMBROIDERY: "embroidery",
};

export function createDefaultInput() {
  return {
    artwork: {
      rasterArtworkPathInfo: null,
    },
    // Second print location (back print) — null until added in the wizard or
    // seeded from an existing product on the account edit page.
    secondLocation: null,
    productType: productTypes.APPAREL,
    salesMethod: salesMethods.ONDEMAND,
    productionMethod: productionMethods.DIRECTTOGARMENT,
    placement: "large+centered",
    printGeometry: {
      width: 9,
      height: 14,
      unit: "in",
    },
    printOffset: {
      x: 0,
      y: 0,
      unit: "in",
    },
    campaignStart: null,
    campaignEnd: null,
    numInks: 1,
    inkType: "plastisol",
    inkEffect: "none",
    mockupOptions: {
      noScale: false,
    },
    colors: [],
  };
}

// Screen-print ink choices, per print location: their labels here, their
// per-garment base-price adders from the server (pricingTables.inkTypeCosts /
// inkEffectCosts, i.e. App\Support\PrintLocation::INK_TYPE_COSTS /
// INK_EFFECT_COSTS). screenPrintInkOptions() joins the two.
const screenPrintInkTypeLabels = {
  plastisol: {
    title: "Standard",
    heading: "Standard",
    description: "Vibrant color, solid look and feel.",
  },
  "water-based": {
    title: "Water-Based",
    heading: "Water-Based",
    description: "Vintage color, premium hand-feel.",
  },
};

const screenPrintInkEffectLabels = {
  none: {
    title: "None",
    heading: "None",
    description: "No special effects. Plain (beautiful) Jane.",
  },
  foil: {
    title: "Foil",
    heading: "Foil",
    description: "Delicate and brilliant, foil makes your design shine.",
  },
};

function screenPrintInkOptions(labels, costs) {
  return Object.fromEntries(
    Object.entries(labels).map(([name, label]) => {
      const cost = costs[name] ?? 0;
      const description = cost > 0 ? `${label.description} (+$${cost})` : label.description;

      return [name, { name, ...label, description, cost }];
    }),
  );
}

// The ink type / ink effect options, priced from a `pricingTables` prop
// (ProductPricing::wizardPricingTables).
export function screenPrintInkTypes(pricingTables) {
  return screenPrintInkOptions(screenPrintInkTypeLabels, pricingTables.inkTypeCosts);
}

export function screenPrintInkEffects(pricingTables) {
  return screenPrintInkOptions(screenPrintInkEffectLabels, pricingTables.inkEffectCosts);
}

export function createDefaultData() {
  return {
    dropzoneRasterOptions: {
      url: UPLOAD_URL,
      maxFilesize: 100,
      maxFiles: 1,
      timeout: 600000,
      acceptedFiles: ".png",
      addRemoveLinks: true,
      createImageThumbnails: false,
      binaryBody: false,
      previewTemplate: DROPZONE_PREVIEW_TEMPLATE,
      error: showDropzoneError,
      params: () => ({ type: "raster" }),
      headers: {
        "Cache-Control": null,
        "X-Requested-With": null,
        "X-XSRF-TOKEN": getXsrfToken(),
      },
    },

    // Screen print's colour-separated source file — the primary's (wizard)
    // and a back print's (wizard + edit page).
    dropzoneSourceOptions: {
      url: UPLOAD_URL,
      maxFilesize: 300,
      maxFiles: 1,
      timeout: 600000,
      acceptedFiles: ".ai,.psd,.pdf,.svg,.eps",
      addRemoveLinks: true,
      createImageThumbnails: false,
      binaryBody: false,
      previewTemplate: DROPZONE_PREVIEW_TEMPLATE,
      error: showDropzoneError,
      params: () => ({ type: "source" }),
      headers: {
        "Cache-Control": null,
        "X-Requested-With": null,
        "X-XSRF-TOKEN": getXsrfToken(),
      },
    },

    rasterFile: null,
    isRasterFileUploading: false,
    rasterGeometry: {
      width: 1,
      height: 1,
    },
    noTrim: false,
    originalUploadMeta: null,

    // Second print location upload machinery — mirrors the primary's
    // rasterFile/noTrim/originalUploadMeta pattern above. Consumed by the
    // mixin's dzSecondRasterArtwork* handlers (shared wizard ↔ edit page).
    secondRasterFile: null,
    isSecondRasterFileUploading: false,
    // A screen-printed back print's colour-separated source file (PRO-3173).
    secondSourceFile: null,
    isSecondSourceFileUploading: false,
    secondRasterGeometry: {
      width: 1,
      height: 1,
    },
    secondNoTrim: false,
    secondOriginalUploadMeta: null,

    // Collapse state for the top-level options-containers (child panels
    // manage their own via NylonOptionPanel's `collapsible`). Pages override
    // individual keys for their mount state (the edit page starts with the
    // Back and Product Details boxes folded).
    collapsedBoxes: {
      product: false,
      artwork: false,
      secondLocation: false,
      colors: false,
      details: false,
    },

    debouncedInput: {
      printGeometry: {
        width: 9,
        height: 14,
        unit: "in",
      },
      printOffset: {
        x: 0,
        y: 0,
        unit: "in",
      },
    },

    // Debounce buffer for the back print's size/offset fields (the mixin's
    // onSecond* handlers write through this into input.secondLocation).
    debouncedSecondInput: {
      printGeometry: {
        width: 9,
        height: 14,
        unit: "in",
      },
      printOffset: {
        x: 0,
        y: 0,
        unit: "in",
      },
    },

    isChoosingColor: false,
    modalData: null,
    renderStatusCache: {},
    submitMessage: "",
    isSubmitting: false,
    showMobilePreview: false,
    synchronizePrices: true,
  };
}

export const placementToBlankLocationMap = {
  "max+centered": "front",
  "large+centered": "front",
  "medium+centered": "front",
  "small+centered": "front",
  "custom+centered": "front",
  "back+max+centered": "back",
  "back+large+centered": "back",
  "back+medium+centered": "back",
  "back+small+centered": "back",
  "back+custom+centered": "back",
  "back+neck+custom+centered": "back",
  pocket: "pocket",
  "pocket+large": "pocket",
  "pocket+medium": "pocket",
  "pocket+small": "pocket",
  "pocket+custom": "pocket",
};

export const placementToBoxMap = {
  "max+centered": { width: 11, height: 14 },
  "large+centered": { width: 9, height: 14 },
  "medium+centered": { width: 7, height: 14 },
  "small+centered": { width: 4, height: 14 },
  "back+max+centered": { width: 11, height: 14 },
  "back+large+centered": { width: 9, height: 14 },
  "back+medium+centered": { width: 7, height: 14 },
  "back+small+centered": { width: 4, height: 14 },
  pocket: { width: 4, height: 3 },
  "pocket+large": { width: 3.75, height: 3.75 },
  "pocket+medium": { width: 2.5, height: 2.5 },
  "pocket+small": { width: 1.25, height: 1.25 },
};

export const modalIds = {
  blankModalId: Symbol("blankModalId"),
  sourceFileModalId: Symbol("sourceFileModalId"),
  placementModalId: Symbol("placementModalId"),
  colorsModalId: Symbol("colorsModalId"),
  campaignSettingsModalId: Symbol("campaignSettingsModalId"),
  inkNumOptionsModalId: Symbol("inkNumOptionsModalId"),
  inkTypeOptionsModalId: Symbol("inkTypeOptionsModalId"),
  titleModalId: Symbol("titleModalId"),
  descriptionModalId: Symbol("descriptionModalId"),
  pricingModalId: Symbol("pricingModalId"),
  accountWhyModalId: Symbol("accountWhyModalId"),
  notesModalId: Symbol("notesModalId"),
};

export function sortFunctionFactory(sortOrder, propertyName) {
  function getNestedProperty(obj, props) {
    if (props.length === 1) {
      return obj[props[0]];
    }

    const newProps = props.slice();
    const prop = newProps.shift();
    return getNestedProperty(obj[prop], newProps);
  }

  if (typeof propertyName === "string" || propertyName instanceof String) {
    propertyName = [propertyName];
  }

  return (a, b) => {
    const aIndex = sortOrder.indexOf(getNestedProperty(a, propertyName));
    const bIndex = sortOrder.indexOf(getNestedProperty(b, propertyName));

    if (aIndex < bIndex) {
      return -1;
    } else if (aIndex > bIndex) {
      return 1;
    }

    return 0;
  };
}

// The back-neck print location — its own placement, mirroring Monster's
// treatment of BN as a distinct location from FB. Keep in step with
// App\Services\Submission::BACK_NECK_PLACEMENT.
export const backNeckPlacement = "back+neck+custom+centered";

// Every placement that carries seller-entered custom dims + offsets, on any
// location. Keep in step with App\Services\Submission::CUSTOM_PLACEMENTS.
export const customPlacements = ["custom+centered", "pocket+custom", "back+custom+centered", backNeckPlacement];

// How far a custom placement may be nudged toward the collar, in inches: from
// where the art sits by default up to the highest point its fulfiller prints.
// Monster (DTG) prints no higher than its own offset (2in front, 3in back), so
// only short front art, which floats down, has room; Clockwise prints up to
// half an inch from the collar. A front design's default top floats from 2in
// (4in tall or more) down to 4in for a sliver. Mirrors
// App\Services\Submission::maxUpwardOffsetInches.
export function maxUpwardOffsetInches(placement, productionMethod, artworkHeightInches) {
  let top = null;

  if (placement === "custom+centered") {
    top = artworkHeightInches >= 4 ? 2 : 4 - artworkHeightInches / 2;
  } else if (placement === "back+custom+centered") {
    top = 3;
  }

  if (top === null) {
    return 0;
  }

  const highest = productionMethod === productionMethods.DIRECTTOGARMENT ? (placement === "custom+centered" ? 2 : 3) : 0.5;

  return Math.max(top - highest, 0);
}

// Sales method => the production methods whose apparel may carry a second
// (back) print location. Mirrors App\Models\Product::SECOND_LOCATION_METHODS.
const secondLocationMethods = {
  [salesMethods.ONDEMAND]: [productionMethods.DIRECTTOGARMENT],
  [salesMethods.CAMPAIGN]: [productionMethods.SCREENPRINT],
};

export function allowsSecondLocation(productType, salesMethod, productionMethod) {
  return productType === productTypes.APPAREL && (secondLocationMethods[salesMethod] ?? []).includes(productionMethod);
}

// Whether apparel of this method may print its only location on the back.
// Mirrors App\Models\Product::BACK_PRIMARY_METHODS.
export function allowsBackPrimary(productType, productionMethod) {
  return productType === productTypes.APPAREL && [productionMethods.DIRECTTOGARMENT, productionMethods.SCREENPRINT].includes(productionMethod);
}

// The Front | Back choice for the primary location, in display order.
export const primarySideOptions = [
  { value: "front", label: "Front" },
  { value: "back", label: "Back" },
];

// Monster's maximum print area for the BN (back neck) location (10x12) — the
// clamp the server enforces (Submission::BN_MAX_PRINT_INCHES) and the collar
// frame the Mockup Editor draws. One definition, in the geometry lib.
export const bnMaxPrintInches = BN_MAX_PRINT_INCHES;

export function getCustomSize(productType, placement) {
  let size = {
    default: {
      width: 1,
      height: 1,
    },
    max: {
      width: 1,
      height: 1,
    },
    min: {
      width: 1,
      height: 1,
    },
    step: 1,
  };

  if (productType === productTypes.APPAREL) {
    const hitLocation = placementToBlankLocationMap[placement];

    if (hitLocation === "pocket") {
      size = {
        default: {
          width: 3.75,
          height: 3.75,
        },
        max: {
          width: 3.75,
          height: 3.75,
        },
        min: {
          width: 1,
          height: 1,
        },
        step: 0.25,
      };
    } else if (placement === backNeckPlacement) {
      // Monster's BN print area is smaller than the back's 11x14 — the
      // server rejects anything larger (Submission::assertValidCollarPrint).
      size = {
        default: { ...bnMaxPrintInches },
        max: { ...bnMaxPrintInches },
        min: {
          width: 1,
          height: 1,
        },
        step: 1,
      };
    } else if (hitLocation === "front" || hitLocation === "back") {
      size = {
        default: {
          width: 11,
          height: 14,
        },
        max: {
          width: 11,
          height: 14,
        },
        min: {
          width: 1,
          height: 1,
        },
        step: 1,
      };
    }
  } else if (productType === productTypes.HAT) {
    size = {
      default: {
        width: 5.5,
        height: 2.25,
      },
      max: {
        width: 5.5,
        height: 2.25,
      },
      min: {
        width: 0.25,
        height: 0.25,
      },
      step: 0.25,
    };
  } else if (productType === productTypes.BEANIE) {
    size = {
      default: {
        width: 3.75,
        height: 2.25,
      },
      max: {
        width: 3.75,
        height: 2.25,
      },
      min: {
        width: 0.25,
        height: 0.25,
      },
      step: 0.25,
    };
  } else if (productType === productTypes.TOTE) {
    size = {
      default: {
        width: 10,
        height: 12,
      },
      max: {
        width: 10,
        height: 12,
      },
      min: {
        width: 1,
        height: 1,
      },
      step: 1,
    };
  } else if (productType === productTypes.STICKER) {
    size = {
      default: {
        width: 4,
        height: 3,
      },
      max: {
        width: 4,
        height: 3,
      },
      min: {
        width: 1,
        height: 1,
      },
      step: 0.1,
    };
  }

  return size;
}

// The area a custom placement's vertical nudge stays inside, per headwear type
// (PRO-1011): Clockwise's 5.5 × 2.25in cap front, and a beanie's max placement
// (3.75 × 2.25in), which keeps the art 0.375in clear of the cuff's edges. Keep in
// step with App\Services\Submission::NUDGE_AREA_INCHES, which says why.
const nudgeAreaInches = {
  [productTypes.HAT]: { width: 5.5, height: 2.25 },
  [productTypes.BEANIE]: { width: 3.75, height: 2.25 },
};

// Whether a product type nudges vertically only (no Offset X).
export function nudgesVerticallyOnly(productType) {
  return productType in nudgeAreaInches;
}

// The Offset X / Y inputs' range (inches) for a custom placement. A hat or
// beanie nudges vertically only (PRO-1011): its art is centred in the nudge
// area and may move half the room left above and below it, measured on the
// artwork as fitted to the custom box (the box itself before an upload).
// Submission::maxYOffsetInches is the same limit unrounded, and clamps again
// server side. Apparel keeps ±1in, except upward: no higher than the
// fulfiller prints (maxUpwardOffsetInches), floored to the hundredth.
export function getOffsetRange(productType, printGeometry, rasterGeometry, placement = null, productionMethod = null) {
  const area = nudgeAreaInches[productType];
  const width = Number(printGeometry.width);
  const height = Number(printGeometry.height);
  let artworkHeight = height;

  if (rasterGeometry?.width > 0 && rasterGeometry?.height > 0 && width > 0 && height > 0) {
    artworkHeight = calculateArtworkGeometry(1, printGeometry, rasterGeometry).artworkHeightInches;
  }

  if (!area) {
    const up = productType === productTypes.APPAREL && Number.isFinite(artworkHeight) ? Math.floor(maxUpwardOffsetInches(placement, productionMethod, artworkHeight) * 100) / 100 : 1;

    return { min: { x: -1, y: -1 }, max: { x: 1, y: up }, step: 0.1 };
  }

  // Whole 1/8in steps, so 0 and every value the spinner reaches stay valid.
  const step = 0.125;
  const y = Number.isFinite(artworkHeight) ? Math.max(0, Math.floor((area.height - artworkHeight) / 2 / step) * step) : 0;

  return { min: { x: 0, y: -y }, max: { x: 0, y }, step };
}

// Back placement choices — for a second print location (PRO-3088) or a
// back-only product's one location (PRO-3173): the four standard back
// placements, the back neck, and Custom (which matches the front's
// custom-size behavior).
export function getBackPlacements(productType, productionMethod) {
  if (!allowsBackPrimary(productType, productionMethod)) {
    return null;
  }

  return [
    {
      name: "back+max+centered",
      iconName: "placement-back-max",
      heading: "Max Centered",
      sizeTitle: "11 in.",
    },
    {
      name: "back+large+centered",
      iconName: "placement-back-large",
      heading: "Large Centered",
      sizeTitle: "9 in.",
    },
    {
      name: "back+medium+centered",
      iconName: "placement-back-medium",
      heading: "Medium Centered",
      sizeTitle: "7 in.",
    },
    {
      name: "back+small+centered",
      iconName: "placement-back-small",
      heading: "Small Centered",
      sizeTitle: "4 in.",
    },
    {
      name: backNeckPlacement,
      iconName: "placement-back-neck",
      heading: "Back Neck",
      sizeTitle: "10 in.",
    },
    {
      name: "back+custom+centered",
      iconName: "placement-back-custom",
      heading: "Custom",
    },
  ];
}

export function getPlacements(productType, productionMethod) {
  let placements = null;

  if (productType === productTypes.APPAREL) {
    if (productionMethod === productionMethods.EMBROIDERY) {
      placements = [
        {
          name: "pocket+large",
          iconName: "placement-pocketLarge",
          heading: "Large Pocket",
          sizeTitle: "3.75 in.",
        },
        {
          name: "pocket+medium",
          iconName: "placement-pocketMedium",
          heading: "Medium Pocket",
          sizeTitle: "2.5 in.",
        },
        {
          name: "pocket+small",
          iconName: "placement-pocketSmall",
          heading: "Small Pocket",
          sizeTitle: "1.25 in.",
        },
        {
          name: "pocket+custom",
          iconName: "placement-pocketCustom",
          heading: "Custom Pocket",
        },
      ];
    } else if (productionMethod === productionMethods.SCREENPRINT || productionMethod === productionMethods.DIRECTTOGARMENT) {
      placements = [
        {
          name: "max+centered",
          iconName: "placement-max",
          heading: "Max Centered",
          sizeTitle: "11 in.",
        },
        {
          name: "large+centered",
          iconName: "placement-large",
          heading: "Large Centered",
          sizeTitle: "9 in.",
        },
        {
          name: "medium+centered",
          iconName: "placement-medium",
          heading: "Medium Centered",
          sizeTitle: "7 in.",
        },
        {
          name: "small+centered",
          iconName: "placement-small",
          heading: "Small Centered",
          sizeTitle: "4 in.",
        },
        {
          name: "pocket",
          iconName: "placement-pocket",
          heading: "Pocket",
          sizeTitle: "4 in.",
        },
        {
          name: "custom+centered",
          iconName: "placement-custom",
          heading: "Custom",
        },
      ];
    }
  } else if (productType === productTypes.PHONECASE) {
    placements = [
      {
        name: "max+centered",
        iconName: "placement-phoneCase-max",
        heading: "Max",
      },
      {
        name: "large+centered",
        iconName: "placement-phoneCase-large",
        heading: "Large",
        sizeTitle: "3 in.",
      },
      {
        name: "medium+centered",
        iconName: "placement-phoneCase-medium",
        heading: "Medium",
        sizeTitle: "2.25 in.",
      },
      {
        name: "small+centered",
        iconName: "placement-phoneCase-small",
        heading: "Small",
        sizeTitle: "1.5 in.",
      },
      {
        name: "extraSmall+centered",
        iconName: "placement-phoneCase-extraSmall",
        heading: "Extra Small",
        sizeTitle: "1 in.",
      },
    ];
  } else if (productType === productTypes.HAT) {
    placements = [
      {
        name: "max+centered",
        iconName: "placement-hat-xl",
        heading: "Max Centered",
        sizeTitle: "2.25 in.",
      },
      {
        name: "large+centered",
        iconName: "placement-hat-l",
        heading: "Large Centered",
        sizeTitle: "2 in.",
      },
      {
        name: "medium+centered",
        iconName: "placement-hat-m",
        heading: "Medium Centered",
        sizeTitle: "1.5 in.",
      },
      {
        name: "small+centered",
        iconName: "placement-hat-s",
        heading: "Small Centered",
        sizeTitle: "1 in.",
      },
      {
        name: "extraSmall+centered",
        iconName: "placement-hat-xs",
        heading: "Extra Small",
        sizeTitle: "0.5 in.",
      },
      {
        name: "custom+centered",
        iconName: "placement-hat-custom",
        heading: "Custom",
      },
    ];
  } else if (productType === productTypes.BEANIE) {
    placements = [
      {
        name: "max+centered",
        iconName: "placement-beanie-xl",
        heading: "Max Centered",
        sizeTitle: "2.25 in.",
      },
      {
        name: "large+centered",
        iconName: "placement-beanie-l",
        heading: "Large Centered",
        sizeTitle: "2 in.",
      },
      {
        name: "medium+centered",
        iconName: "placement-beanie-m",
        heading: "Medium Centered",
        sizeTitle: "1.5 in.",
      },
      {
        name: "small+centered",
        iconName: "placement-beanie-s",
        heading: "Small Centered",
        sizeTitle: "1 in.",
      },
      {
        name: "extraSmall+centered",
        iconName: "placement-beanie-xs",
        heading: "Extra Small",
        sizeTitle: "0.5 in.",
      },
      {
        name: "custom+centered",
        iconName: "placement-beanie-custom",
        heading: "Custom",
      },
    ];
  } else if (productType === productTypes.TOTE) {
    placements = [
      {
        name: "max+centered",
        iconName: "placement-tote-max",
        heading: "Max Centered",
        sizeTitle: "10 in.",
      },
      {
        name: "large+centered",
        iconName: "placement-tote-l",
        heading: "Large Centered",
        sizeTitle: "9 in.",
      },
      {
        name: "medium+centered",
        iconName: "placement-tote-m",
        heading: "Medium Centered",
        sizeTitle: "7 in.",
      },
      {
        name: "small+centered",
        iconName: "placement-tote-s",
        heading: "Small Centered",
        sizeTitle: "4 in.",
      },
    ];
  } else if (productType === productTypes.DRINKWARE) {
    placements = [
      {
        name: "max+centered",
        iconName: "placement-drinkware-max",
        heading: "Max",
      },
      {
        name: "extraLarge+centered",
        iconName: "placement-drinkware-extraLarge",
        heading: "Extra Large",
        sizeTitle: "1/3 cir.",
      },
      {
        name: "large+centered",
        iconName: "placement-drinkware-large",
        heading: "Large",
        sizeTitle: "2/9 cir.",
      },
      {
        name: "medium+centered",
        iconName: "placement-drinkware-medium",
        heading: "Medium",
        sizeTitle: "1/6 cir.",
      },
      {
        name: "small+centered",
        iconName: "placement-drinkware-small",
        heading: "Small",
        sizeTitle: "1/9 cir.",
      },
      {
        name: "extraSmall+centered",
        iconName: "placement-drinkware-extraSmall",
        heading: "Extra Small",
        sizeTitle: "1/12 cir.",
      },
    ];
  } else if (productType === productTypes.STICKER) {
    placements = [
      {
        name: "max+centered",
        iconName: "placement-sticker-max",
        heading: "Max",
      },
    ];
  }

  return placements;
}
